import { MessageHandlerService } from './../../core/utility/message-handler.service';
import { AppointmentService } from './../../core/service/appointment.service';
import { Component, OnInit, Output, EventEmitter, Input, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { EventEdit } from '../model/event';
import moment from 'moment';
import { SettingService } from 'src/app/core/service/setting.service';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { Subscription } from 'rxjs';
import { MemberService } from 'src/app/core/service/member.service';
import { Address, Member } from 'src/app/member/model/member';
import { ProviderService } from 'src/app/core/service/provider.service';
import { TranslateService } from '@ngx-translate/core';
import { AppConfiguration, AppConfigService } from 'src/app/core/service/app-config.service';
import { FacilityService } from 'src/app/core/service/facility.service';
import { FacilityEdit } from 'src/app/facility/model/facility-edit';
import { Dictionary } from 'src/app/core/model/dictionary';
import { UserService } from 'src/app/core/service/user.service';
@Component({
  standalone: false,
  selector: 'app-event-edit-appointment',
  templateUrl: './event-edit-appointment.component.html',
  styleUrls: ['./event-edit-appointment.component.scss']
})
export class EventEditAppointmentComponent implements OnInit, OnDestroy
{
  @Input() public isNew = false;
  @Input() public memberId: number;

  @Output() public cancelling = new EventEmitter();
  @Output() public save = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public eventEdit: any;
  public isCancelled = false;
  public patientLabel = {};
  public showMapTitle: string;
  public channelsDirectoryDialogOpened = false;
  public allowSelection = true;
  public singleSelectionOnly = true;
  public doNotResetScrollOnClose = true;
  public disableEscape = true;
  public deleteDialogOpened = false;
  public currentMember: Member;
  public facilityName: string;
  public mapUrl: string;
  public providerAddress: Address;
  public saving = false;

  private openComponentAddAppointmentSubscription: Subscription;
  private appConfig: AppConfiguration;

  constructor(
    private fb: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private appointmentService: AppointmentService,
    private settingService: SettingService,
    private messengerService: MessengerService,
    private memberService: MemberService,
    private providerService: ProviderService,
    private translateService: TranslateService,
    private appConfigService: AppConfigService,
    private facilityService: FacilityService,
    private userService: UserService
  )
  {
    // subscribe to open new message dialog within the component
    this.openComponentAddAppointmentSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'openComponentAddAppointmentDialog')
        {
          this.isNew = true;
          this.memberId = message.data.memberId;
          this.initModel(message.data.editDataItemAppointment);
        }
      }
    });
  }

  public get showMap(): boolean
  {
    return (this.appConfig && this.appConfig.google && this.appConfig.google.mapBaseUrl !== null &&
      this.currentMember && this.currentMember.address &&
      this.currentMember.address.latitude !== 0 && this.currentMember.address.longitude !== 0);
  }

  public get id(): UntypedFormControl
  {
    return this.editForm.get('id');
  }

  public get title(): UntypedFormControl
  {
    return this.editForm.get('title');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get start(): UntypedFormControl
  {
    return this.editForm.get('start');
  }

  public get end(): UntypedFormControl
  {
    return this.editForm.get('end');
  }

  public get version(): UntypedFormControl
  {
    return this.editForm.get('version');
  }

  public get isAllDay(): UntypedFormControl
  {
    return this.editForm.get('isAllDay');
  }

  public get npi(): UntypedFormControl
  {
    return this.editForm.get('npi');
  }

  public get provider(): UntypedFormControl
  {
    return this.editForm.get('provider');
  }

  public get sendAppointmentToPatient(): UntypedFormControl
  {
    return this.editForm.get('sendAppointmentToPatient');
  }

  public get facility(): UntypedFormControl
  {
    return this.editForm.get('facility');
  }

  @Input() public set model(event: any)
  {
    this.initModel(event);
  }

  public ngOnDestroy(): void
  {
    if (this.openComponentAddAppointmentSubscription !== undefined) { this.openComponentAddAppointmentSubscription.unsubscribe(); }
  }

  public ngOnInit(): void
  {
    this.appConfig = this.appConfigService.getConfig();
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel.toLowerCase() };
        this.translateService.get(['SHOW-MAP-TITLE']).subscribe({
          next: (res) =>
          {
            this.showMapTitle = res['SHOW-MAP-TITLE'].replace('{{patientLabel}}', sessionSetting.patientLabel);
          }
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Close
   */
  public closeAppointment(): void
  {
    // Close dialog
    this.cancelling.emit();
    this.editFormActive = false;
    this.mapUrl = undefined;
    this.facilityName = undefined;
    this.providerAddress = null;
    document.body.style.overflow = '';
  }

  public deleteAppointment(): void
  {
    // Validate date range
    if (this.id.value !== null)
    {
      this.saving = true;
      this.appointmentService.deleteAppointment(this.id.value).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('APPOINTMENT-DELETED-SUCCESSFULLY');
          this.save.emit();
        },
        error: (errorResponse) =>
        {
          this.saving = false;
          this.messageHandlerService.displayError(errorResponse);
        },
        complete: () =>
        {
          this.saving = false;
          this.editFormActive = false;
          this.deleteDialogOpened = false;
          this.facilityName = null;
          this.providerAddress = null;
          document.body.style.overflow = '';
        }
      });
    }
  }

  public saveAppointment(): void
  {
    // Validate date range
    if (this.start.value !== null && this.end.value !== null)
    {
      // adding 1 day due a error in end and start values
      const d1 = moment(this.end.value).add(1, 'days');
      const d2 = moment(this.start.value).add(1, 'days');
      let diff = d1.diff(d2, 'minutes');
      if (diff <= 0)
      {
        this.messageHandlerService.displayError('DATE-RANGE-ERROR');
        return;
      }

      // Validate appointment date for new appointment only. Otherwise we can't edit.
      if (this.isNew)
      {
        diff = d2.diff(moment(), 'minutes');
        if (diff < 0)
        {
          this.messageHandlerService.displayError('APPOINTMENT-MUST-USE-FUTURE-DATE');
          return;
        }
      }
    }

    const objectToPersist = new EventEdit();
    objectToPersist.id = this.isNew ? 0 : this.id.value;
    objectToPersist.memberId = this.memberId;
    objectToPersist.details = this.description.value;
    objectToPersist.version = this.version.value ? this.version.value : 0;
    objectToPersist.eventDateTime = moment(this.start.value).toISOString();
    objectToPersist.eventEndDateTime = moment(this.end.value).toISOString();
    objectToPersist.appointment.title = this.title.value;
    objectToPersist.appointment.allDay = this.isAllDay.value;
    objectToPersist.appointment.sendAppointmentToPatient = this.canNotifyPatient() ? this.sendAppointmentToPatient.value : false;
    objectToPersist.appointment.id = this.isNew ? 0 : this.eventEdit.dataItem.appointment.id;
    objectToPersist.appointment.version = this.isNew ? 0 : this.eventEdit.dataItem.appointment.version;
    objectToPersist.appointment.npi = this.npi.value;
    objectToPersist.appointment.provider = this.provider.value;
    objectToPersist.appointment.timezoneOffset = new Date(this.start.value).getTimezoneOffset();
    objectToPersist.appointment.facilityId = this.facility.value;

    this.saving = true;
    this.appointmentService.saveAppointment(objectToPersist, this.isNew, objectToPersist.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('APPOINTMENT-SAVED-SUCCESSFULLY');
        this.save.emit();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
        this.saving = false;
      },
      complete: () =>
      {
        this.saving = false;
        this.editFormActive = false;
        this.providerAddress = null;
        this.facilityName = null;
        document.body.style.overflow = '';
      }
    });
  }

  public onSelectProviders(provider: any[]): void
  {
    if (provider.length === 1)
    {
      this.npi.setValue(provider[0].npi);
      this.provider.setValue(provider[0].fullName.replaceAll(/ /g, '') !== '' ? provider[0].fullName : provider[0].organization);
      this.facility.setValue(provider[0].facilityId);
      this.provider.disable();    // Prevent changing the name
      this.editForm.markAsDirty();
    }
    this.channelsDirectoryDialogOpened = false;
    this.loadAddress();
  }

  public showProvider(): void
  {
    this.channelsDirectoryDialogOpened = true;
  }

  public loadAddress(): void
  {
    if (this.facility.value)
    {
      this.facilityService.getFacilityById(this.facility.value).subscribe({
        next: (facility: FacilityEdit) =>
        {
          if (facility.address !== null && facility.address.latitude !== 0 && facility.address.longitude !== 0)
          {
            if (this.showMap)
            {
              this.mapUrl = `${this.appConfig.google.mapBaseUrl}/dir/?api=1&origin=${this.currentMember.address.latitude},
                              ${this.currentMember.address.longitude}&destination=${facility.address.latitude},
                              ${facility.address.longitude}`;
            }
            else
            {
              this.mapUrl = `${this.appConfig.google.mapBaseUrl}/search/?api=1&query=${facility.address.latitude},
                              ${facility.address.longitude}`;
            }
            this.providerAddress = facility.address;
            this.facilityName = facility.name;
          }
          return true;
        }
      });
    }
    else if (this.npi.value)
    {
      this.providerService.getProviderByNPI(this.npi.value).subscribe({
        next: (providers: any) =>
        {
          if (providers && providers.total === 1 && providers.data[0].practiceAddress &&
            providers.data[0].practiceAddress.latitude !== 0 &&
            providers.data[0].practiceAddress.longitude !== 0)
          {
            if (this.showMap)
            {
              this.mapUrl = `${this.appConfig.google.mapBaseUrl}/dir/?api=1&origin=${this.currentMember.address.latitude},
                              ${this.currentMember.address.longitude}&destination=${providers.data[0].practiceAddress.latitude},
                              ${providers.data[0].practiceAddress.longitude}`;
            }
            else
            {
              this.mapUrl = `${this.appConfig.google.mapBaseUrl}/search/?api=1&query=${providers.data[0].practiceAddress.latitude},
                              ${providers.data[0].practiceAddress.longitude}`;
            }
            this.providerAddress = new Address();
            this.providerAddress.country = new Dictionary();
            this.providerAddress.state = new Dictionary();
            this.providerAddress.city = providers.data[0].practiceAddress.city;
            this.providerAddress.country.displayName = providers.data[0].practiceAddress.country;
            this.providerAddress.county = providers.data[0].practiceAddress.county;
            this.providerAddress.postalCode = providers.data[0].practiceAddress.postalCode;
            this.providerAddress.state.code = providers.data[0].practiceAddress.state;
            this.providerAddress.streetLine = providers.data[0].practiceAddress.streetLine;
            this.providerAddress.streetLineAdditional = providers.data[0].practiceAddress.streetLineAdditional;
            this.facilityName = providers.data[0].organization;
          }
          else
          {
            this.messageHandlerService.displayError('NO-PROVIDER-BY-NPI-FOUND');
          }
          return true;
        }
      });
    }
  }

  public openMap(): void
  {
    window.open(this.mapUrl, '_blank');
  }

  public onBlurStartDate(): void
  {
    if (this.start.value !== '' && this.start.value !== null && this.end.value === null)
    {
      this.end.setValue(moment(this.start.value).add(30, 'minutes').toDate());
    }
  }

  /**
   * This method clear the previous selected provider from the search result or entered manually.
   */
  public removeProvider(): void
  {
    this.npi.setValue(null);
    this.facility.setValue(null);
    this.provider.setValue(null);
    this.provider.enable();
    this.editForm.markAsDirty();
    this.mapUrl = undefined;
    this.facilityName = undefined;
    this.providerAddress = null;
  }

  /**
   * Do not allow sending patient email if set to no notification or null.
   *
   * @returns Boolean Return true if patient notification is allowed.
   */
  private canNotifyPatient(): boolean
  {
    return this.currentMember.notificationType !== null && this.currentMember.notificationType !== 'NO_NOTIFICATION';
  }

  private initModel(event: any)
  {
    if (event === undefined) { return; }

    if (event.provider !== undefined && event.provider.userId)
    {
      this.userService.getUserById(event.provider.userId, false).subscribe({
        next: (user) =>
        {
          if (user.deleted === true)
          {
            event.provider = undefined;
          }
        }
      });
    }

    this.memberService.getMember(this.memberId).subscribe({
      next: (member) =>
      {
        this.currentMember = member;
        this.isCancelled = this.isNew ? false :
          !(event.dataItem.appointment.status === 'SCHEDULED' || event.dataItem.appointment.status === 'CONFIRMED');
        this.editForm = this.fb.group({
          id: [{ value: event.id, disabled: this.isCancelled }],
          start: [{
            value: event.start === null || event.start === undefined ? null : new Date(event.start),
            disabled: this.isCancelled
          }, Validators.required],
          end: [{
            value: event.end === null || event.end === undefined ? null : new Date(event.end),
            disabled: this.isCancelled
          }, Validators.required],
          title: [{ value: event.title, disabled: this.isCancelled }, Validators.required],
          description: [{ value: event.description, disabled: this.isCancelled }],
          isAllDay: [{ value: this.isNew ? false : event.dataItem.appointment.allDay, disabled: this.isCancelled }],
          sendAppointmentToPatient: [{
            value: this.isNew ? false : event.dataItem.appointment.sendAppointmentToPatient,
            disabled: this.isCancelled || !this.canNotifyPatient()
          }],
          version: [event.version],
          npi: [event.provider !== undefined ? event.provider.npi :
            (event.dataItem !== undefined && event.dataItem.appointment !== undefined) ? event.dataItem.appointment.npi : null],
          provider: [{
            value: event.provider !== undefined ? (event.provider.userFullName !== null ?
              event.provider.userFullName : event.provider.firstName + ' ' + event.provider.lastName) :
              event.dataItem !== undefined && event.dataItem.appointment !== undefined ? event.dataItem.appointment.provider : null,
            disabled: (this.isCancelled || event.provider !== undefined ||
              (event.dataItem !== undefined
                && event.dataItem.appointment.provider !== undefined && event.dataItem.appointment.npi) ? true : false)
          }, Validators.required],
          facility: [(event.dataItem !== undefined && event.dataItem.appointment !== undefined) ?
            event.dataItem.appointment.facilityId : null]
        });

        this.eventEdit = event;
        this.editFormActive = true;
        this.saving = false;

        document.body.style.overflow = 'hidden';

        this.loadAddress();
      }
    });
  }
}
